import { isNil, isString, isUndefined } from 'lodash';
import { AW_URL_QUERY_LIST_PARAM, AW_URL_QUERY_PARAM } from './UrlUtils';

export type PageFilter = {
  colTag: string;
  values: string[];
  type: PageFilterDomainType;
};

export type PageFilterDetails = {
  pageFilters: PageFilter[];
  page: string | null;
  pageHierarchy: string[];
  validation: {
    hasMissingPageFilter: boolean;
    hasMissingPageKey: boolean;
    hasInvalidSubValue: boolean;
  };
  isPageFilterInteractivityScenario: boolean;
};

export const isPageKeyType = (pageValue: string, supportedPageKeys: string[]) => supportedPageKeys.includes(pageValue);

export enum PageFilterDomainType {
  ACTION_TYPE = 'AC',
  MODEL_EVALUATOR_TYPE = 'ME',
}

export const isPageFilterDomainType = (type: string): type is PageFilterDomainType =>
  Object.values<string>(PageFilterDomainType).filter<string>(isString).includes(type);

export enum SubValueKey {
  COLTAG = 'c',
  TYPE = 't',
  VALUE = 'v',
}

const isCompletePageFilter = (partialPageFilter: Partial<PageFilter>): partialPageFilter is PageFilter =>
  !isNil(partialPageFilter.colTag) && !isNil(partialPageFilter.values) && !isNil(partialPageFilter.type);

export const FILTER_VALUE_CATEGORY_SEPARATOR = ';';
// E.g., separator for key-value pair "v:Moderately Conservative|Moderate"
export const FILTER_SUB_VALUE_KV_PATTERN_SEPARATOR = ':';
export const PAGE_HIERARCHY_SEPARATOR = '/';
export const FILTER_V_VALUES_SEPARATOR = '|';

export const parsePageFilterIntegrationUrl = (supportedPageKeys: string[]): PageFilterDetails => {
  // Internally perform decodeURIComponent
  const urlParams = new URLSearchParams(window.location.search);
  const pageParam: string | null = urlParams.get(AW_URL_QUERY_PARAM.PAGE);
  const pageHierarchy: string[] = isNil(pageParam) ? [] : pageParam.split(PAGE_HIERARCHY_SEPARATOR);
  // Example Pattern: f=c:metadata.account.ACCOUNT_RISK_PROFILE;v:Moderately Conservative|Moderate;t:AC
  const filterPatternList: string[] = urlParams.getAll(AW_URL_QUERY_LIST_PARAM.FILTER);
  const page =
    !isUndefined(pageHierarchy[0]) && isPageKeyType(pageHierarchy[0], supportedPageKeys) ? pageHierarchy[0] : null;
  let hasInvalidSubValue = false;
  const pageFilters = filterPatternList.reduce<PageFilter[]>((total, filterPattern: string) => {
    const subValues: string[] = filterPattern.split(FILTER_VALUE_CATEGORY_SEPARATOR);
    const pageFilter: Partial<PageFilter> = {};
    subValues.forEach((subValue) => {
      const keyValuePair = subValue.split(FILTER_SUB_VALUE_KV_PATTERN_SEPARATOR);
      if (keyValuePair.length === 2) {
        const [key, value] = keyValuePair as [string, string]; // guard by above
        if (key === SubValueKey.COLTAG) {
          pageFilter.colTag = value;
        } else if (key === SubValueKey.VALUE) {
          pageFilter.values = value?.split(FILTER_V_VALUES_SEPARATOR);
        } else if (key === SubValueKey.TYPE) {
          if (isPageFilterDomainType(value)) {
            pageFilter.type = value;
          }
        }
      } else {
        console.warn(`Invalid sub value - ${subValue}`);
        hasInvalidSubValue = true;
      }
    });
    if (isCompletePageFilter(pageFilter)) {
      total.push(pageFilter);
    } else {
      console.warn(`Invalid page filter - ${JSON.stringify(filterPattern)}`);
    }
    return total;
  }, []);
  const hasMissingPageKey = isNil(page);
  const hasMissingPageFilter = pageFilters.length !== filterPatternList.length;
  const isPageFilterInteractivityScenario = !hasMissingPageKey && !hasMissingPageFilter && !hasInvalidSubValue;
  return {
    pageFilters,
    page,
    pageHierarchy,
    validation: {
      hasMissingPageFilter,
      hasMissingPageKey,
      hasInvalidSubValue,
    },
    isPageFilterInteractivityScenario,
  };
};
